import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLock } from '../context/LockContext';
import { useToast } from '../context/ToastContext';
import './PinPad.css';
import './ChangePinModal.css';

function ChangePinModal({ onClose }) {
  const { t } = useTranslation();
  const { changePin } = useLock();
  const { showToast } = useToast();
  const [step, setStep] = useState('current'); // 'current', 'new', 'confirm'
  const [currentPin, setCurrentPin] = useState('');
  const [newPin, setNewPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [error, setError] = useState('');
  const [shake, setShake] = useState(false);

  // Refs for reading latest pin values inside async callbacks
  const currentPinRef = useRef(currentPin);
  const newPinRef = useRef(newPin);
  currentPinRef.current = currentPin;
  newPinRef.current = newPin;

  const getCurrentPin = () => {
    if (step === 'current') return currentPin;
    if (step === 'new') return newPin;
    if (step === 'confirm') return confirmPin;
    return '';
  };

  const handleChangePinSubmit = useCallback(async (current, newP, confirm) => {
    if (newP !== confirm) {
      setError(t('lock.pinMismatch'));
      setShake(true);
      setTimeout(() => {
        setNewPin('');
        setConfirmPin('');
        setStep('new');
        setShake(false);
      }, 1000);
      return;
    }

    try {
      await changePin(current, newP);
      setError('');
      showToast(t('toast.pinChanged'));
      onClose();
    } catch (err) {
      setError(err.code === 'INCORRECT_CURRENT_PIN'
        ? t('lock.incorrectCurrentPin')
        : t('lock.changeError'));
      setShake(true);
      setTimeout(() => {
        setCurrentPin('');
        setNewPin('');
        setConfirmPin('');
        setStep('current');
        setShake(false);
      }, 1500);
    }
  }, [changePin, onClose, showToast, t]);

  // Use functional setState to avoid stale closures on rapid clicks
  const handleNumberClick = useCallback((num) => {
    setError('');
    setShake(false);

    const appendDigit = (setter, onComplete) => {
      setter(prev => {
        if (prev.length >= 4) return prev;
        const newValue = prev + num;
        if (newValue.length === 4 && onComplete) {
          setTimeout(onComplete, 200);
        }
        return newValue;
      });
    };

    if (step === 'current') {
      appendDigit(setCurrentPin, () => setStep('new'));
    } else if (step === 'new') {
      appendDigit(setNewPin, () => setStep('confirm'));
    } else if (step === 'confirm') {
      appendDigit(setConfirmPin, () => {
        // Read latest values via refs since step transitions don't cause stale closures
        handleChangePinSubmit(currentPinRef.current, newPinRef.current, confirmPin.length === 3 ? confirmPin + num : confirmPin);
      });
    }
  }, [step, confirmPin, handleChangePinSubmit]);

  const handleBackspace = useCallback(() => {
    setError('');
    setShake(false);

    if (step === 'current') {
      setCurrentPin(prev => prev.slice(0, -1));
    } else if (step === 'new') {
      setNewPin(prev => prev.slice(0, -1));
    } else if (step === 'confirm') {
      setConfirmPin(prev => prev.slice(0, -1));
    }
  }, [step]);

  const handleBack = () => {
    setError('');
    setShake(false);

    if (step === 'confirm') {
      setConfirmPin('');
      setStep('new');
    } else if (step === 'new') {
      setNewPin('');
      setStep('current');
    }
  };

  // Physical keyboard support
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (/^[0-9]$/.test(e.key)) {
        e.preventDefault();
        handleNumberClick(e.key);
      } else if (e.key === 'Backspace') {
        e.preventDefault();
        handleBackspace();
      } else if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      } else if (e.key === 'Enter') {
        e.preventDefault();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleNumberClick, handleBackspace, onClose]);

  const currentValue = getCurrentPin();

  const getTitle = () => {
    if (step === 'current') return t('lock.enterCurrentPin');
    if (step === 'new') return t('lock.enterNewPin');
    if (step === 'confirm') return t('lock.confirmNewPin');
    return '';
  };

  return (
    <>
      <div className="modal-overlay" onClick={onClose}></div>
      <div className="change-pin-modal">
        <div className="modal-header">
          <h2>{t('lock.changePin')}</h2>
          <button className="modal-close-btn" onClick={onClose}>✕</button>
        </div>

        <div className="modal-body">
          <div className="pin-step-indicator">
            <div className={`step ${step === 'current' ? 'active' : step !== 'current' ? 'completed' : ''}`}>1</div>
            <div className="step-line"></div>
            <div className={`step ${step === 'new' ? 'active' : step === 'confirm' ? 'completed' : ''}`}>2</div>
            <div className="step-line"></div>
            <div className={`step ${step === 'confirm' ? 'active' : ''}`}>3</div>
          </div>

          <h3 className="pin-title">{getTitle()}</h3>

          {error && (
            <div className="pin-error" role="alert">
              {error}
            </div>
          )}

          <div className={`pin-dots ${shake ? 'shake' : ''}`}>
            {[0, 1, 2, 3].map(index => (
              <div
                key={index}
                className={`pin-dot ${currentValue.length > index ? 'filled' : ''}`}
              />
            ))}
          </div>

          <div className="pin-keypad">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(num => (
              <button
                key={num}
                className="pin-button"
                onClick={() => handleNumberClick(num.toString())}
              >
                {num}
              </button>
            ))}
            {step !== 'current' ? (
              <button
                className="pin-button back-button"
                onClick={handleBack}
                aria-label={t('common.back', 'Back')}
              >
                <span className="material-icons" aria-hidden="true">arrow_back</span>
              </button>
            ) : (
              <div className="pin-button empty" aria-hidden="true" />
            )}
            <button
              className="pin-button"
              onClick={() => handleNumberClick('0')}
            >
              0
            </button>
            <button
              className="pin-button backspace"
              onClick={handleBackspace}
              aria-label={t('lock.backspace', 'Backspace')}
            >
              <span className="material-icons" aria-hidden="true">backspace</span>
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

export default ChangePinModal;
